import { Button, Checkbox, Col, ColorPicker, ColorPickerProps, GetProp, Input, Row, Select, Table } from "antd";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { formatStatus } from "../saveCnSoftAccess";
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'

export interface Status {
    name: string,
    color: string,
    defaultProspect?: boolean,
    defaultClient?: boolean,
    tendance?: "Prospect" | "Client",
    modification?: 'added' | 'deleted' | 'modified',
    readonly?: boolean,
    readonly_reason?: string
}

type Color = GetProp<ColorPickerProps, 'value'>;

export const defaultValueStatus: Status[] = [{
    "name": "Prospect",
    "color": "#ff5630",
    "defaultProspect": true
},
{
    "name": "Client",
    "color": "#00a76f",
    "defaultClient": true
}]

interface CnStatusProps {
    initialStatus: Status[] | null;
}

export interface CnStatusHandle {
    getSaveData: () => string;
}

const CnStatus = forwardRef<CnStatusHandle, CnStatusProps>(function CnStatus({ initialStatus }, ref) {

    const oldStatus = useRef<Status[]>([]);
    const [status, setStatus] = useState<Status[]>([]);

    useImperativeHandle(ref, () => ({
        getSaveData: () => formatStatus(status),
    }), [status]);

    const columns = [
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, _: Status, index: number) => <Input
                value={text}
                disabled={status[index].modification === 'deleted'}
                onChange={(e) => {
                    setStatus(status.map((s, i) => {
                        if (i === index) {
                            return { ...s, name: e.target.value, modification: index < oldStatus.current.length ? (e.target.value !== oldStatus.current[i].name ? 'modified' : undefined) : 'added' };
                        }
                        return s;
                    }));
                }}
            />,
        },
        {
            title: 'Couleur',
            dataIndex: 'color',
            key: 'color',
            render: (color: Color, _: Status, index: number) => (
                <ColorPicker
                    disabled={status[index].modification === 'deleted'}
                    value={color}
                    disabledAlpha
                    onChange={(color) => {
                        setStatus(status.map((s, i) => {
                            if (i === index) {
                                return { ...s, color: "#" + color.toHex() };
                            }
                            return s;
                        }));
                    }}
                />
            )
        },
        {
            title: 'Défaut client',
            dataIndex: 'defaultClient',
            key: 'defaultClient',
            render: (defaultClient: boolean | undefined) => (
                <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px" }}>
                    {defaultClient == true && (
                        <CheckOutlined style={{ color: "green", margin: "auto" }} />
                    )}
                </div>
            )
        },
        {
            title: 'Défaut prospect',
            dataIndex: 'defaultProspect',
            key: 'defaultProspect',
            render: (defaultProspect: boolean | undefined) => (
                <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px" }}>
                    {defaultProspect == true && (
                        <CheckOutlined style={{ color: "green", margin: "auto" }} />
                    )}
                </div>
            )
        },
        {
            title: 'Tendance',
            dataIndex: 'tendance',
            key: 'tendance',
            render: (tendance: "Prospect" | "Client" | undefined, _: Status, index: number) => (
                <Select
                    disabled={status[index].defaultClient || status[index].defaultProspect || status[index].modification === 'deleted'}
                    value={tendance}
                    onChange={(value) => {
                        setStatus(status.map((s, i) => {
                            if (i === index) {
                                return { ...s, tendance: value };
                            }
                            return s;
                        }));
                    }}
                >
                    <Select.Option value="Prospect">Prospect</Select.Option>
                    <Select.Option value="Client">Client</Select.Option>
                </Select>
            )
        },
        {
            title: 'Readonly',
            dataIndex: 'readonly',
            key: 'readonly',
            render: (_: any, s: Status, index: number) => (
                <Row >
                    <Col span={24}>
                        <Checkbox
                            checked={s.readonly}
                            onChange={(e) => {
                                setStatus(status.map((s, i) => {
                                    if (i === index) {
                                        return { ...s, readonly: e.target.checked };
                                    }
                                    return s;
                                }));
                            }}
                        />
                    </Col>
                    <Col span={24}>
                        <Input
                            value={s.readonly_reason}
                            onChange={(e) => {
                                setStatus(status.map((s, i) => {
                                    if (i === index) {
                                        return { ...s, readonly_reason: e.target.value };
                                    }
                                    return s;
                                }));
                            }}
                        />
                    </Col>
                </Row>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, s: Status, index: number) => (
                <Button
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    disabled={s.defaultClient || s.defaultProspect || s.modification === 'deleted'}
                    onClick={() => {
                        if (status[index].modification === 'added') {
                            setStatus(status.filter((_, i) => i !== index));
                            return;
                        }
                        setStatus(status.map((e, i) => i == index ? { ...e, modification: 'deleted', name: oldStatus.current[i].name } : e));
                    }}
                />
            )
        }
    ];

    useEffect(() => {
        const value = initialStatus
            ? initialStatus.map((e: any) => ({ ...e, tendance: e.tendance ? e.tendance : (e.defaultClient ? "Client" : "Prospect") }))
            : defaultValueStatus;
        setStatus(value);
        oldStatus.current = value;
    }, [initialStatus]);

    return (
        <div>
            <Table
                pagination={false}
                dataSource={status}
                columns={columns}
                size="small"
            />
            <br />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button
                    icon={<PlusOutlined />}
                    disabled={status.length >= 10}
                    type='primary'
                    shape='round'
                    onClick={() => setStatus([...status, { name: "", color: "#000000", modification: 'added', tendance: "Prospect" }])}
                >
                    Ajouter un statut
                </Button>
            </div>
        </div>
    );
});

export default CnStatus;
